import type { Appointment } from '@medplum/fhirtypes';
import { getDisplayString } from '@medplum/core';
import { Alert, Button, Center, Loader, Stack, Text } from '@mantine/core';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffectivePortalPatientId } from '@/hooks/usePortalSubjectPatientId';
import {
  CHIME_JOIN_OPENS_MINUTES,
  chimeJoinNotYetOpen,
  telehealthSessionHasFinished,
} from '@/lib/telehealth/joinWindow';
import { ClientChimeSession } from './ClientChimeSession';
import {
  appointmentRequiresLocationConfirmation,
  loadPatientForAddressPrefill,
  patientHomeAddressPrefill,
} from '@/lib/sessionLocation/clientLocationConfirmationService';
import { confirmClientSessionLocation } from '@/lib/telehealth/chime/chimeTelehealthService';
import { resolveAppointmentModality } from '@/pages/appointments/utils/appointmentModality';
import { ClientLocationConfirmationModal } from './components/ClientLocationConfirmationModal';
import { JoinNotOpenScreen } from './components/JoinNotOpenScreen';
import type { ClientLocationConfirmationFormInput } from '@/schemas/client-location-confirmation.schema';
import type { LocationPrefillSource } from '@/lib/sessionLocation/types';

export function ClientTelehealthJoinPage(): JSX.Element {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const patientId = useEffectivePortalPatientId();

  const [checkingLocation, setCheckingLocation] = useState(true);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [locationConfirmed, setLocationConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAddressPrefill, setSavedAddressPrefill] = useState<ReturnType<typeof patientHomeAddressPrefill>>(null);
  const [fhirAppointment, setFhirAppointment] = useState<Appointment | null>(null);
  const [chimeReady, setChimeReady] = useState(false);
  // The appointment could not be read; Retry bumps `loadAttempt` to read it again.
  const [loadError, setLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  // Bumped when the join window opens, so a page left on the "not open yet" screen re-checks it.
  const [, setJoinWindowTick] = useState(0);
  const recheckJoinWindow = useCallback(() => setJoinWindowTick((t) => t + 1), []);

  const clientName = (profile ? getDisplayString(profile) : '') || 'Client';

  useEffect(() => {
    if (!appointmentId || !patientId) {
      setCheckingLocation(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setCheckingLocation(true);
      setLoadError(false);
      try {
        const [{ required, existing, appointment: appt }, patient] = await Promise.all([
          appointmentRequiresLocationConfirmation(medplum, appointmentId, patientId),
          loadPatientForAddressPrefill(medplum, patientId),
        ]);
        if (cancelled) return;
        setFhirAppointment(appt);
        setSavedAddressPrefill(patientHomeAddressPrefill(patient));
        if (existing || !required) {
          setLocationConfirmed(true);
          setLocationModalOpen(false);
        } else {
          setLocationModalOpen(true);
        }
      } catch {
        // Not "not a telehealth visit": nothing was read, so say so and offer Retry.
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setCheckingLocation(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appointmentId, loadAttempt, patientId, medplum]);

  const isTelehealth = useMemo(() => {
    if (!fhirAppointment) return false;
    const modality = resolveAppointmentModality(fhirAppointment);
    return modality === 'telehealth' || modality === 'hybrid';
  }, [fhirAppointment]);

  const startIso = fhirAppointment?.start;
  const endIso = fhirAppointment?.end;
  // Follows the bot's close time.
  const finished = startIso ? telehealthSessionHasFinished(startIso, endIso) : false;
  // The client may wait from 15 minutes before start, so "hasn't started yet" means any time before start
  // (the bot's `earlyJoin`).
  const beforeStart = startIso ? new Date(startIso).getTime() > Date.now() : false;

  const handleLocationConfirmed = useCallback(
    async (
      values: ClientLocationConfirmationFormInput & {
        prefillSource: LocationPrefillSource;
        wasEditedAfterPrefill: boolean;
        ipDerivedCity?: string;
        ipDerivedState?: string;
      },
    ) => {
      if (!appointmentId || !patientId) return;
      setSaving(true);
      setSaveError(null);
      try {
        const input = {
          street: values.street,
          city: values.city,
          state: values.state,
          postalCode: values.postalCode,
          notAtUsualAddress: values.notAtUsualAddress,
          attestationAccepted: values.attestationAccepted,
          prefillSource: values.prefillSource,
          wasEditedAfterPrefill: values.wasEditedAfterPrefill,
          ipDerivedCity: values.ipDerivedCity,
          ipDerivedState: values.ipDerivedState,
        };
        // The bot writes it to the appointment's Encounter (patients cannot update it), re-reading on a
        // version conflict so it cannot clobber what other writers saved meanwhile.
        await confirmClientSessionLocation(medplum, appointmentId, input);
        medplum.invalidateSearches('Appointment');
        setLocationConfirmed(true);
        setLocationModalOpen(false);
        setChimeReady(true);
      } catch {
        setSaveError(
          'We could not save your location confirmation. Please try again before joining the session.',
        );
      } finally {
        setSaving(false);
      }
    },
    [appointmentId, patientId, medplum],
  );

  const handleJoinClick = useCallback(() => {
    if (locationConfirmed) {
      setChimeReady(true);
      return;
    }
    setLocationModalOpen(true);
  }, [locationConfirmed]);

  if (!appointmentId) {
    return (
      <Center h="60vh">
        <Text c="dimmed">No appointment specified.</Text>
      </Center>
    );
  }

  // A running call stays mounted past the scheduled end and through any later re-checks.
  if (chimeReady) {
    return (
      <ClientChimeSession
        appointmentId={appointmentId}
        clientName={clientName}
        onLeave={() => navigate('/client-portal')}
      />
    );
  }

  if (checkingLocation) {
    return (
      <Center h="60vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="sm" c="dimmed" ta="center" maw={360}>
            Preparing your session…
          </Text>
        </Stack>
      </Center>
    );
  }

  if (loadError) {
    return (
      <Center h="60vh">
        <Stack align="center" gap="sm" maw={400}>
          <Text c="dimmed" ta="center">
            We could not load this appointment. Check your connection, then try again.
          </Text>
          <Button variant="light" onClick={() => setLoadAttempt((n) => n + 1)}>
            Retry
          </Button>
        </Stack>
      </Center>
    );
  }

  if (!isTelehealth) {
    return (
      <Center h="60vh">
        <Text c="dimmed">This appointment is not a telehealth visit.</Text>
      </Center>
    );
  }

  if (finished) {
    return (
      <Center h="60vh">
        <Text c="dimmed" ta="center" maw={400}>
          This session has already finished.
        </Text>
      </Center>
    );
  }

  // The bot refuses joins this early, so do not let the client start the location check.
  if (startIso && chimeJoinNotYetOpen(startIso)) {
    return (
      <JoinNotOpenScreen
        startIso={startIso}
        opensMinutesBefore={CHIME_JOIN_OPENS_MINUTES}
        onOpen={recheckJoinWindow}
        onBack={() => navigate('/client-portal')}
      />
    );
  }

  return (
    <>
      <Center h="60vh">
        <Stack align="center" gap="md">
          <Text size="lg" fw={500}>
            Ready to join your telehealth session
          </Text>
          {beforeStart ? (
            <Alert color="yellow" maw={400}>
              Your session hasn&apos;t started yet. You can wait here — your provider will admit
              you when they&apos;re ready.
            </Alert>
          ) : null}
          <Button size="lg" onClick={handleJoinClick}>
            Join session
          </Button>
        </Stack>
      </Center>

      <ClientLocationConfirmationModal
        opened={locationModalOpen}
        onClose={() => {
          if (!saving) {
            setLocationModalOpen(false);
            navigate('/client-portal');
          }
        }}
        onConfirmed={handleLocationConfirmed}
        savedAddressPrefill={savedAddressPrefill}
        saving={saving}
        saveError={saveError}
      />
    </>
  );
}
